/**
 * Sticky Booking Bar
 * Mobile-first sticky CTA that drives conversions
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BookAppointmentLink from './BookAppointmentLink';
import { CLEANING_START_CAT } from '../data/pricing';
import { trackPhoneClick } from '../utils/analytics';

const StickyBookingBar: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  // Don't show on book page
  const isBookPage = location.pathname === '/book';

  // Show after scrolling
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsVisible(scrollPosition > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isBookPage) return null;

  return (
    <>
      {/* Mobile Sticky Bar */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 transform transition-all duration-300 ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Main bar */}
        <div className="bg-white border-t shadow-[0_-4px_20px_rgba(0,0,0,0.15)] px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Price preview */}
            <div className="flex-shrink-0">
              <p className="text-[10px] text-gray-500 uppercase font-medium">From</p>
              <p className="text-lg font-bold text-hellotend-neutral-800 leading-tight">${CLEANING_START_CAT}</p>
            </div>

            {/* CTAs */}
            <div className="flex-1 flex gap-2">
              <BookAppointmentLink
                analyticsSource="sticky_booking_bar"
                className="flex-1 bg-gradient-to-r from-brand-teal to-teal-600 text-white py-3 rounded-lg font-bold text-center text-sm shadow-lg active:scale-95 transition-transform"
              >
                Book Now
              </BookAppointmentLink>
              <a
                href="tel:+19544663775"
                onClick={() => trackPhoneClick('sticky_booking_bar')}
                className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-brand-teal active:scale-95 transition-transform"
                aria-label="Call us"
              >
                <i className="fas fa-phone text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Floating Button */}
      <div
        className={`hidden lg:block fixed bottom-8 right-8 z-50 transform transition-all duration-500 ${
          isVisible ? 'translate-x-0 opacity-100' : 'translate-x-32 opacity-0'
        }`}
      >
        <div className="relative group">
          {/* Pulse effect */}
          <div className="absolute inset-0 bg-brand-teal rounded-full animate-ping opacity-25"></div>
          
          {/* Main button */}
          <BookAppointmentLink
            analyticsSource="sticky_booking_bar"
            className="relative flex items-center gap-3 bg-gradient-to-r from-brand-teal to-teal-600 text-white px-6 py-4 rounded-full font-bold shadow-2xl hover:shadow-3xl hover:scale-105 transition-all"
          >
            <i className="fas fa-calendar-alt text-lg" aria-hidden="true"></i>
            <span>Book Now</span>
          </BookAppointmentLink>
        </div>
      </div>

      {/* Spacer for mobile to prevent content being hidden */}
      <div className="lg:hidden h-24"></div>
    </>
  );
};

export default StickyBookingBar;

